import { useLocalStorageValue } from '@react-hookz/web';
import { MNEMONICS } from 'constants/mnemonics';
import uniqueId from 'lodash/uniqueId';
import {
  StyleOptionValues,
  WellHomePlotlyConfig,
  WellHomePlotlyMobileConfig,
} from 'pages/WellHome/components/OperationalPlots/components/WellHomeOperationalGraphs/utils/PlotsBuilderHelper';
import { useEffect, useState } from 'react';
import { useIsMobile } from 'hooks/useIsMobile';
import * as Yup from 'yup';

import { WELL_HOME_PLOT_CONFIG } from '.';

export const getInitialPlotConfig = (): WellHomePlotlyConfig => [
  [
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.DEPTH,
      lineStyle: StyleOptionValues.SOLID,
      color: 'ORANGE',
      rangeByWellbore: {},
    },
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.BITDEP,
      lineStyle: StyleOptionValues.SOLID,
      color: 'BLUE',
      rangeByWellbore: {},
    },
  ],
  [
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.BLOCK_POSITION,
      lineStyle: StyleOptionValues.SOLID,
      color: 'GREEN',
      rangeByWellbore: {},
    },
  ],
  [
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.HKLDAV,
      lineStyle: StyleOptionValues.SOLID,
      color: 'GOLD',
      rangeByWellbore: {},
    },
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.WOBAV,
      lineStyle: StyleOptionValues.DOT,
      color: 'WHITE',
      rangeByWellbore: {},
    },
  ],
  [
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.TQAV,
      lineStyle: StyleOptionValues.DOT,
      color: 'PRUPLE',
      rangeByWellbore: {},
    },
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.RPM,
      lineStyle: StyleOptionValues.SOLID,
      color: 'BROWN',
      rangeByWellbore: {},
    },
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.FLOWINAV,
      lineStyle: StyleOptionValues.SOLID,
      color: 'YELLOW',
      rangeByWellbore: {},
    },
  ],
  [
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.ECDPWD,
      lineStyle: StyleOptionValues.DOT,
      color: 'ORANGE',
      rangeByWellbore: {},
    },
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.PRESPUMPPAV,
      lineStyle: StyleOptionValues.SOLID,
      color: 'RED',
      rangeByWellbore: {},
    },
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.GASOUTAV,
      lineStyle: StyleOptionValues.DOT,
      color: 'PINK',
      rangeByWellbore: {},
    },
  ],
];

const getInitialMobilePlotConfig = () => [
  [
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.DEPTH,
      lineStyle: StyleOptionValues.SOLID,
      color: 'ORANGE',
      rangeByWellbore: {},
    },
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.BITDEP,
      lineStyle: StyleOptionValues.SOLID,
      color: 'BLUE',
      rangeByWellbore: {},
    },
  ],
  [
    {
      key: uniqueId(),
      mnemonic: MNEMONICS.BLOCK_POSITION,
      lineStyle: StyleOptionValues.SOLID,
      color: 'GREEN',
      rangeByWellbore: {},
    },
  ],
];

export const useGetAndValidateWellHomePlotConfig = () => {
  const { isMobile } = useIsMobile();
  const plotConfigData: WellHomePlotlyConfig | WellHomePlotlyMobileConfig = isMobile
    ? getInitialMobilePlotConfig() as WellHomePlotlyMobileConfig
    : getInitialPlotConfig() as WellHomePlotlyConfig;
  const [validatedPlotConfig, setValidatedPlotConfig] =
    useState< WellHomePlotlyConfig | WellHomePlotlyMobileConfig >(plotConfigData);
  const [plotConfig, setPlotConfig] = useLocalStorageValue< WellHomePlotlyConfig | WellHomePlotlyMobileConfig >(
    WELL_HOME_PLOT_CONFIG,
    plotConfigData,
  );
  const validationSchema = Yup.array().of(
    Yup.array().of(
      Yup.object().shape({
        key: Yup.string().required(),
        mnemonic: Yup.string().required(),
        rangeByWellbore: Yup.object().required(),
        color: Yup.string().required(),
        lineStyle: Yup.string().required(),
      }),
    ),
  );

  useEffect(() => {
    try {
      validationSchema.validateSync(plotConfig);
      setValidatedPlotConfig(plotConfig);
    } catch {
      setPlotConfig(plotConfigData);
    }
  }, [plotConfig, setPlotConfig, validationSchema]);

  return { plotConfig: validatedPlotConfig, setPlotConfig };
};
